import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Tabs, Tab, Stack } from '@mui/material';
import { useQuery, useLazyQuery } from '@apollo/client';
import { Tooltip } from '@mui/material';
import { v4 as uuid } from 'uuid'

import CardPreview from 'packages/ui/CardPreview';
import CardActionButton from 'components/global/CardActions/CardActionButton';
import { CardHeader } from 'packages/ui/CardPreview';
import ScrollTable from 'components/global/CUK/ScrollTable';
import TableViewBase from 'packages/ui/Table/Table';
import { useTable } from 'packages/ui/Table';

import useSort from '../../hooks/use-sort';
import { GET_CENTER_DOCUMENT_VIEWS, GET_CENTER_DOCUMENT_DOWNLOAD, GET_CENTER_DOCUMENT } from '../../operations-gql/queries';
import { useNavigate } from 'react-router-dom';
import SitesSelection from 'packages/ui/Arborist/components/SitesSelection';

const ViewersTable = ({ documentId }) => {
  const sortApi = useSort({ defaultOrderBy: 'activity_at' });
  const { loading, error, data } = useQuery(GET_CENTER_DOCUMENT_VIEWS, {
    variables: { id: documentId },
  });

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  const COLUMNS_CONFIG = useMemo(() => [
    {
      name: 'infos',
      header: {
        label: () => 'INFOS'
      },
      value: item => (
        <Box>
          <Typography variant="body2" sx={{ fontSize: '12px' }}>{`${item.user.firstname} ${item.user.lastname}`}</Typography>
          <Typography variant="body2" sx={{ color: 'grey.400', fontSize: '12px' }}>{item.user.email}</Typography>
        </Box>
      ),
      sortable: false,
      width: '60%',
    },
    {
      name: 'activity_at',
      header: {
        label: () => 'DATE'
      },
      value: item => {
        const date = new Date(item.activity_at);
        return (
          <Tooltip title={formatDateTime(item.activity_at)} placement="top">
            <span>{`${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`}</span>
          </Tooltip>
        );
      },
      sortBy: 'activity_at',
      sortable: true,
      width: '40%',
    }
  ], []);

  const views = useMemo(() => {
    if (!data) return [];
    return data.views.map(view => ({uuid: uuid(), ...view}))
  }, [data])
  const sortedItems = useMemo(() => sortApi.sort(views), [sortApi, data]);

  const apiTable = useTable({
    items: sortedItems,
    columns: COLUMNS_CONFIG,
  });

  const handleSortChange = useCallback((name) => {
    const column = COLUMNS_CONFIG.find((column) => column.name === name);
    sortApi.orderByChange(column.sortBy || column.name);
  }, [COLUMNS_CONFIG, sortApi]);

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography>Error: {error.message}</Typography>;

  return (
    <Stack
      sx={{
        overflow: 'hidden',
        overflowY: 'auto',
        flex: '1 1 auto',
        scrollbarWidth: 'none',
        height: '100%',
        border: 0,
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        '& .MuiTableCell-head': {
          backgroundColor: '#FAFAFA',
        },
        '& .MuiTable-root': {
          tableLayout: 'fixed',
          width: '100%',
        },
      }}
    >
      <ScrollTable ssid>
        <TableViewBase
          apiTable={apiTable}
          sort={{
            by: sortApi.orderBy,
            direction: sortApi.orderDirection,
            onChange: handleSortChange,
          }}
          sx={{
            '& .MuiTableCell-root': {
              padding: '12px 16px',
            },
            '& .MuiTableCell-head': {
              fontWeight: 600,
            },
            '& .MuiTableCell-body': {
              borderBottom: '1px solid #E0E0E0',
            },
            '& .MuiTableRow-root:last-child .MuiTableCell-body': {
              borderBottom: 'none',
            },
          }}
        />
      </ScrollTable>
    </Stack>
  );
};

const Card = ({ id, onClose }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();

  const { loading: documentLoading, error: documentError, data: documentData } = useQuery(GET_CENTER_DOCUMENT, {
    variables: { id },
    skip: !id,
  });

  const [getDownloadUrl, { loading: downloadLoading }] = useLazyQuery(GET_CENTER_DOCUMENT_DOWNLOAD);

  const data = documentData?.document;

  const formatContentType = (contentType) => {
    const upperContentType = (contentType || '').toUpperCase();
    if (upperContentType.includes('VND.OPENXMLFORMATS-OFFICEDOCUMENT.SPREADSHEETML.SHEET')) {
      return 'XLS / XLSX';
    }
    return upperContentType.split('/').pop();
  };

  const handleEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/documents/center/${id}/edit`);
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (data?.content_type === 'application/pdf') {
      const { data: downloadData } = await getDownloadUrl({ variables: { id } });
      if (downloadData && downloadData.getCenterDocumentDownload) {
        window.open(downloadData.getCenterDocumentDownload.url, '_blank');
      }
    }
  };

  const handleView = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { data: downloadData } = await getDownloadUrl({ variables: { id } });
    if (downloadData && downloadData.getCenterDocumentDownload) {
      window.open(downloadData.getCenterDocumentDownload.url, '_blank');
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/documents/center/${id}/delete`);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  const formatFileSize = (bytes) => {
    const mo = bytes / (1024 * 1024);
    return `${mo.toFixed(2)} Mo`;
  };

  return (
    <CardPreview
      title={t('ui_elements.card_title')}
      padding={2}
      onClose={onClose}
      loading={documentLoading}
    >
      {!documentError && data && (
        <>
          <Stack sx={{ px: 2 }}>
            <CardHeader>
              <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>{data.name}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <Typography variant="body2" sx={{ color: 'grey.400', fontSize: '12px', fontWeight: 500, display: 'inline' }}>{data.document_type}</Typography>
                <Typography variant="body2" sx={{ color: 'content.secondary', fontSize: '12px', fontWeight: 300, ml: 1, display: 'inline' }}>
                  {t('Importé par')} <span style={{ color: 'black', fontWeight: 500 }}>{`${data.user.firstname} ${data.user.lastname}`}</span>, le {formatDate(data.updated_at)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'content.secondary', fontSize: '12px', fontWeight: 500 }}>
                  {formatContentType(data.content_type)} - {formatFileSize(data.file.size)}
                </Typography>
              </Box>
            </CardHeader>
            <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'center', my: 2 }}>
              <CardActionButton
                tooltiptitle={t('audin_document.table.actions.download')}
                iconweight="solid"
                iconlabel="arrow-down"
                onClick={handleDownload}
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                }}
              />
              <CardActionButton
                tooltiptitle={t('audin_document.table.actions.edit')}
                iconlabel="pen"
                onClick={handleEdit}
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                }}
              />
              {data.content_type === 'application/pdf' && (
                <CardActionButton
                  tooltiptitle={t('audin_document.table.actions.view')}
                  iconlabel="eye"
                  onClick={handleView}
                  disabled={downloadLoading}
                  sx={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    width: '32px',
                    height: '32px',
                  }}
                />
              )}
              <CardActionButton
                tooltiptitle={t('audin_document.table.actions.delete')}
                iconlabel="trash"
                onClick={handleDelete}
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                }}
              />
            </Box>
            <Box>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  width: '100%',
                  '& .MuiTab-root': {
                    minHeight: '36px !important',
                    color: 'content.main',
                    fontSize: '12px',
                    textTransform: 'none',
                    '&.Mui-selected': {
                      color: 'primary.main',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'primary.main',
                  },
                  '& .MuiTabs-flexContainer': {
                    display: 'flex',
                    justifyContent: 'flex-start',
                  },
                }}
              >
                <Tab
                  label="Consulté par"
                  sx={{
                    padding: '0 4px',
                  }}
                />
                <Tab
                  sx={{
                    padding: '0 4px',
                  }}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <span>Comptes & Sites</span>
                      <Box
                        sx={{
                          ml: 1,
                          px: 1,
                          py: 0.5,
                          borderRadius: '4px',
                          backgroundColor: '#F7F7F7',
                          color: 'content.main',
                          fontSize: '12px',
                        }}
                      >
                        {data?.accounts.filter(({ is_site }) => is_site).length}
                      </Box>
                    </Box>
                  }
                />
              </Tabs>
            </Box>
          </Stack>
          {activeTab === 0 && <ViewersTable documentId={id} />}
          {activeTab === 1 && (
            <SitesSelection accounts={data.accounts} padding={'0 20px'} />
          )}
        </>
      )}
    </CardPreview>
  );
};

export default Card;
